// =============================================================================
// Tournament bracket layout — geometry shared by BracketRound and BracketCanvas.
// =============================================================================
//
// All round-N card centers align with the midpoint of their two source cards
// in round N-1. This is achieved by doubling the vertical pitch every round.
//
//   pitch_r  = 2^r  *  (cardHeight + rowGap)
//   offset_r = (2^r - 1) * (cardHeight + rowGap) / 2
//   y(r, j)  = j * pitch_r + offset_r            // top edge of card j in round r
// =============================================================================

export const CARD_WIDTH = 246; // matches MatchCard intrinsic width
export const CARD_HEIGHT = 68; // matches MatchCard intrinsic height
export const ROW_GAP = 28; // vertical gap between cards in round 0
export const ROUND_GAP = 72; // horizontal gap between consecutive rounds

export const ROW_HEIGHT = CARD_HEIGHT + ROW_GAP; // = round-0 vertical pitch

export const TOTAL_ROUNDS = 5; // R32 → R16 → QF → SF → Final
export const FINAL_ROUND_INDEX = TOTAL_ROUNDS - 1;

/** Top-edge Y of match `matchIndex` in a standard bracket round. */
export const getMatchY = (roundIndex, matchIndex) => {
  const factor = Math.pow(2, roundIndex);
  const pitch = factor * ROW_HEIGHT;
  const offset = ((factor - 1) * ROW_HEIGHT) / 2;
  return matchIndex * pitch + offset;
};

/**
 * Top-edge Y for the Final round (which is non-standard: it holds two
 * "endpoint" matches — the actual Final and the 3rd-place playoff).
 *
 *   index 0 → Final      (centered between the two semifinal cards)
 *   index 1 → 3° lugar   (one row below the Final card)
 */
export const getFinalRoundY = matchIndex => {
  // Shift up by ROW_HEIGHT/2 so the Final + 3° pair is vertically
  // centered between the two semi-final source cards.
  const finalY = getMatchY(FINAL_ROUND_INDEX, 0) - ROW_HEIGHT / 2;
  return matchIndex === 0 ? finalY : finalY + ROW_HEIGHT;
};

/** Left-edge X of round `roundIndex`'s column. */
export const getRoundX = roundIndex => roundIndex * (CARD_WIDTH + ROUND_GAP);

/** Y of card top edge — picks the right formula depending on the round. */
export const getCardY = (roundIndex, matchIndex) =>
  roundIndex === FINAL_ROUND_INDEX
    ? getFinalRoundY(matchIndex)
    : getMatchY(roundIndex, matchIndex);

// Canvas dimensions for a 5-round, 32-match bracket.
export const CANVAS_WIDTH =
  TOTAL_ROUNDS * CARD_WIDTH + (TOTAL_ROUNDS - 1) * ROUND_GAP;
export const CANVAS_HEIGHT = 16 * CARD_HEIGHT + 15 * ROW_GAP;
